/**
 * Project Management Dashboard
 * 
 * Main entry point for the project management module.
 * Displays list of boards and allows creating new boards.
 * Similar to Focalboard's workspace/board switcher.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { ProjectBoard, UserSession } from '../../types';
import { getBoards, createBoard } from '../../services/projectService';
import KanbanBoard from './KanbanBoard';
import AdminSettings from './AdminSettings';
import './ProjectDashboard.css';

interface ProjectDashboardProps {
  userSession: UserSession;
  onLogout?: () => void;
}

/**
 * Main Project Management Dashboard
 * Handles board selection and creation
 */
const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  userSession,
  onLogout,
}) => {
  const [boards, setBoards] = useState<ProjectBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<ProjectBoard | null>(null);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [newBoardDescription, setNewBoardDescription] = useState('');
  const [newBoardIcon, setNewBoardIcon] = useState('📋');
  const [isCreating, setIsCreating] = useState(false);

  /**
   * Available emoji icons for boards
   */
  const BOARD_ICONS = [
    '📋', '📊', '📈', '🎯', '🚀', '💡', '🔨', '🎨',
    '📝', '🏗️', '🎯', '⚡', '🌟', '🔥', '💼', '🎓',
  ];

  /* useEffect moved down */

  /**
   * Fetch all boards from Firebase
   */
  const loadBoards = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedBoards = await getBoards(userSession.adminId);
      setBoards(fetchedBoards);

      // Auto-select first board if available
      if (fetchedBoards.length > 0 && !selectedBoard) {
        setSelectedBoard(fetchedBoards[0]);
      }
    } catch (err) {
      console.error('Error loading boards:', err);
      setError('Failed to load boards. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [userSession.adminId, selectedBoard]);

  /**
   * Load boards on component mount
   */
  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  /* useEffect moved down */

  /**
   * Handle board creation
   */
  const handleCreateBoard = async () => {
    if (!newBoardTitle.trim()) {
      setError('Board title is required');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const newBoard = await createBoard(
        userSession.adminId,      // Admin ID first
        newBoardTitle,
        newBoardDescription,
        userSession.email,
        newBoardIcon
      );

      setBoards(prev => [newBoard, ...prev]);
      setSelectedBoard(newBoard);
      setShowCreateModal(false);

      // Reset form
      setNewBoardTitle('');
      setNewBoardDescription('');
      setNewBoardIcon('📋');
    } catch (err) {
      console.error('Error creating board:', err);
      setError('Failed to create board. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Handle board selection
   */
  const handleSelectBoard = (board: ProjectBoard) => {
    setSelectedBoard(board);
  };

  /**
   * Handle board update
   */
  const handleBoardUpdate = (updatedBoard: ProjectBoard) => {
    setBoards(prev =>
      prev.map(board => (board.id === updatedBoard.id ? updatedBoard : board))
    );
    if (selectedBoard?.id === updatedBoard.id) {
      setSelectedBoard(updatedBoard);
    }
  };

  /**
   * Go back to board list
   */
  const handleBackToList = () => {
    setSelectedBoard(null);
    setShowAdminSettings(false);
  };

  // Check if user can manage users (show admin settings if admin or has admin tag)
  const canManageUsers = userSession.isAdmin || userSession.tag === 'admin';

  return (
    <div className="project-dashboard">
      {/* Top Navigation Bar */}
      <div className="project-dashboard-navbar">
        <div className="navbar-left">
          {(selectedBoard || showAdminSettings) ? (
            <Button
              variant="link"
              className="back-button"
              onClick={handleBackToList}
            >
              <i className="bi bi-arrow-left me-2"></i>
              All Boards
            </Button>
          ) : (
            <h2 className="dashboard-title">
              <i className="bi bi-kanban me-2"></i>
              Project Management
            </h2>
          )}
        </div>

        <div className="navbar-right">
          <span className="user-email me-3">
            <i className="bi bi-person-circle me-1"></i>
            {userSession.name} ({userSession.tag})
            {!userSession.isAdmin && userSession.adminEmail && (
              <small className="text-muted ms-2">Team of {userSession.adminEmail}</small>
            )}
          </span>

          {canManageUsers && !selectedBoard && !showAdminSettings && (
            <Button variant="outline-primary" size="sm" className="me-2" onClick={() => setShowAdminSettings(true)}>
              <i className="bi bi-gear me-1"></i>
              Admin Settings
            </Button>
          )}

          {onLogout && (
            <Button variant="outline-danger" size="sm" onClick={onLogout}>
              <i className="bi bi-box-arrow-right me-1"></i>
              Logout
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="project-dashboard-content">
        {error && !showCreateModal && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="dashboard-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading boards...</p>
          </div>
        ) : showAdminSettings ? (
          /* Show Admin Settings */
          <AdminSettings userSession={userSession} />
        ) : selectedBoard ? (
          /* Show Selected Board */
          <KanbanBoard
            board={selectedBoard}
            userEmail={userSession.email}
            adminId={userSession.adminId}
            onBoardUpdate={handleBoardUpdate}
          />
        ) : (
          /* Show Board List */
          <Container>
            <div className="boards-header">
              <div>
                <h3>Your Boards</h3>
                <p className="text-muted">
                  Select a board to view or create a new one
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Create Board
              </Button>
            </div>

            {boards.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-kanban display-1 text-muted"></i>
                <h4>No boards yet</h4>
                <p className="text-muted">
                  Create your first board to start managing tasks
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowCreateModal(true)}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Create Your First Board
                </Button>
              </div>
            ) : (
              <Row className="boards-grid">
                {boards.map(board => (
                  <Col key={board.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                    <Card
                      className="board-card"
                      onClick={() => handleSelectBoard(board)}
                    >
                      <Card.Body>
                        <div className="board-card-icon">{board.icon}</div>
                        <Card.Title className="board-card-title">
                          {board.title}
                        </Card.Title>
                        {board.description && (
                          <Card.Text className="board-card-description">
                            {board.description.substring(0, 80)}
                            {board.description.length > 80 && '...'}
                          </Card.Text>
                        )}
                        <div className="board-card-meta">
                          <small className="text-muted">
                            Updated {new Date(board.updatedAt).toLocaleDateString()}
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        )}
      </div>

      {/* Create Board Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => !isCreating && setShowCreateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Board</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Form>
            {/* Board Icon Selection */}
            <Form.Group className="mb-3">
              <Form.Label>Board Icon</Form.Label>
              <div className="board-icon-selector">
                {BOARD_ICONS.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    className={`icon-option ${newBoardIcon === icon ? 'selected' : ''}`}
                    onClick={() => setNewBoardIcon(icon)}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </Form.Group>

            {/* Board Title */}
            <Form.Group className="mb-3">
              <Form.Label>Board Title *</Form.Label>
              <Form.Control
                type="text"
                value={newBoardTitle}
                onChange={(e) => setNewBoardTitle(e.target.value)}
                placeholder="e.g., Product Development, Marketing Campaign"
                autoFocus
                required
              />
            </Form.Group>

            {/* Board Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newBoardDescription}
                onChange={(e) => setNewBoardDescription(e.target.value)}
                placeholder="Describe what this board is for..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateBoard}
            disabled={isCreating || !newBoardTitle.trim()}
          >
            {isCreating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-lg me-2"></i>
                Create Board
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectDashboard;

